import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '@ohif/ui-next';

interface FormDatePickerProps {
  label: string;
  required?: boolean;
  value?: string; // Format: "YYYY-MM-DD"
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const FormDatePicker: React.FC<FormDatePickerProps> = ({
  label,
  required = false,
  value = '',
  onChange,
  placeholder = 'Select A Date',
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const handleInputClick = () => {
    if (!disabled) {
      inputRef.current?.showPicker?.();
      setIsOpen(true);
    }
  };

  const displayValue = value || '';

  // Calendar icon SVG
  const CalendarIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.8333 3.33333H15V1.66667C15 1.20833 14.625 0.833333 14.1667 0.833333C13.7083 0.833333 13.3333 1.20833 13.3333 1.66667V3.33333H6.66667V1.66667C6.66667 1.20833 6.29167 0.833333 5.83333 0.833333C5.375 0.833333 5 1.20833 5 1.66667V3.33333H4.16667C2.875 3.33333 1.91667 4.29167 1.91667 5.58333V15.8333C1.91667 17.125 2.875 18.0833 4.16667 18.0833H15.8333C17.125 18.0833 18.0833 17.125 18.0833 15.8333V5.58333C18.0833 4.29167 17.125 3.33333 15.8333 3.33333ZM15.8333 15.8333H4.16667V8.75H15.8333V15.8333Z"
        fill="#334155"
      />
    </svg>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Label */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          position: 'relative',
        }}
      >
        <label
          style={{
            fontSize: '14px',
            fontWeight: 500,
            color: disabled ? 'var(--label-color-disabled)' : 'var(--label-color-default)',
            lineHeight: '20px',
          }}
        >
          {label}
        </label>
        {required && (
          <span
            style={{
              color: '#dc2626',
              fontSize: '10px',
              position: 'absolute',
              left: '-8px',
              top: '2px',
              fontWeight: 600,
            }}
          >
            *
          </span>
        )}
      </div>

      {/* Input Field */}
      <div
        ref={containerRef}
        style={{ position: 'relative', width: '100%' }}
      >
        <div
          onClick={handleInputClick}
          style={{
            width: '100%',
            height: '36px',
            padding: '8px 12px',
            backgroundColor: disabled ? 'var(--bg-input-disabled)' : 'var(--bg-input-default)',
            border: `1px solid ${error ? '#dc2626' : disabled ? 'var(--border-input-disabled)' : 'var(--border-input-default)'}`,
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 400,
            color: disabled
              ? 'var(--text-input-disabled)'
              : displayValue
                ? 'var(--text-input-default)'
                : '#64748b',
            outline: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: 'inset 0px 2px 4px 0px rgba(0,0,0,0.06)',
          }}
        >
          {/* Calendar Icon */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <CalendarIcon />
          </div>

          {/* Date Input (hidden, used for native date picker) */}
          <input
            ref={inputRef}
            type="date"
            value={displayValue}
            onChange={handleDateChange}
            disabled={disabled}
            max={(() => {
              const today = new Date();
              today.setDate(today.getDate() - 1); // Set to yesterday (one day before today)
              return today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
            })()}
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: disabled ? 'not-allowed' : 'pointer',
              left: 0,
              top: 0,
            }}
          />

          {/* Display Value or Placeholder */}
          <span
            style={{
              flex: 1,
              color: displayValue ? 'var(--text-input-default)' : '#64748b',
              pointerEvents: 'none',
            }}
          >
            {displayValue
              ? new Date(displayValue + 'T00:00:00').toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : placeholder}
          </span>

          {/* Chevron Down Icon */}
          <Icons.ChevronDown
            width="20"
            height="20"
            style={{
              color: '#334155',
              flexShrink: 0,
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <span
          style={{
            fontSize: '12px',
            color: '#dc2626',
            marginTop: '4px',
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default FormDatePicker;
